import { useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, PackageBadge, StatusPill } from "@/components/ui-bits";
import { Search, Gem, Layers, Crown, TrendingUp, Loader2, Network } from "lucide-react";
import { MatrixTree } from "@/components/MatrixTree";
import { SettlementHistory } from "@/components/SilverSettlement";

const Row = ({ label, value, accent = "text-white", bold = false }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-400">{label}</span>
    <span className={`font-num ${accent} ${bold ? "font-bold" : ""}`}>{value}</span>
  </div>
);

export default function AdminSilverCard() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a User ID or email");
    setBusy(true);
    try {
      const r = await API.get("/admin/silver-cards", { params: { q: q.trim() } });
      setD(r.data);
    } catch (e) { setD(null); toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const p = d?.progress;
  const b = d?.breakdown;
  const bars = d ? [
    { label: "Level 1", filled: p.level1_filled, total: p.slots[0], value: b.matrix_values[0], payout: b.level_payouts[0] },
    { label: "Level 2", filled: p.level2_filled, total: p.slots[1], value: b.matrix_values[1], payout: b.level_payouts[1] },
    { label: "Level 3", filled: p.level3_filled, total: p.slots[2], value: b.matrix_values[2], payout: b.level_payouts[2] },
  ] : [];

  return (
    <div>
      <PageHeader title="Silver Card System" subtitle="Search a member by User ID (referral code) or email to inspect their Silver Card level structure and payout." testid="admin-silver-header" />

      <div className="glass rounded-2xl p-3 mb-6 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="h-4 w-4 text-slate-500" />
          <input data-testid="sc-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Enter User ID (e.g. SO123ABC) or email..." className="w-full bg-transparent text-white outline-none py-2" />
        </div>
        <button data-testid="sc-search-btn" onClick={search} disabled={busy}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-slate-900 font-semibold px-5 py-2 rounded-xl">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Search
        </button>
      </div>

      {!d ? (
        <div className="glass rounded-2xl p-12 text-center text-slate-500" data-testid="sc-empty">
          Search a member to view their Silver Card structure.
        </div>
      ) : (
        <>
          <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="sc-member">
            <div>
              <div className="text-white font-semibold">{d.member.name}</div>
              <div className="text-xs text-slate-500">{d.member.email} · {d.member.mobile}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-num text-cyan-300 text-sm">{d.member.referral_code}</span>
              <PackageBadge id={d.member.package_id} />
              <StatusPill status={d.member.status} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard testid="sc-total" label="Silver Cards" value={d.total_cards} icon={Gem} accent="cyan" />
            <StatCard testid="sc-completed" label="Completed" value={d.completed} icon={Layers} accent="emerald" />
            <StatCard testid="sc-earned" label="Total Earned" value={d.total_earned} money icon={TrendingUp} accent="gold" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div className="glass rounded-2xl p-6" data-testid="sc-progress">
              <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-1">
                <Layers className="h-5 w-5 text-cyan-400" /> Level Structure
              </h3>
              <p className="text-xs text-slate-500 mb-5">{p.drop_count} of {p.threshold} boxes filled (fills as Silver Cards are granted below this member).</p>
              <div className="space-y-5">
                {bars.map((row) => {
                  const pct = row.total > 0 ? Math.min(100, (row.filled / row.total) * 100) : 0;
                  return (
                    <div key={row.label} data-testid={`sc-${row.label.toLowerCase().replace(" ", "-")}`}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-slate-300">{row.label} <span className="text-slate-500">· {row.total} × {fmt(row.value)}</span></span>
                        <span className="font-num text-white">{row.filled}/{row.total} · {fmt(row.payout)}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-slate-300 to-cyan-400 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass rounded-2xl p-6" data-testid="sc-breakdown">
              <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-1">
                <Crown className="h-5 w-5 text-amber-400" /> Payout Distribution
              </h3>
              <p className="text-xs text-slate-500 mb-5">On Level-3 completion, {fmt(b.total)} is distributed:</p>
              <div className="space-y-2 text-sm">
                <Row label="Creator wallet" value={fmt(b.creator)} accent="text-amber-300" />
                {b.upline.map((amt, i) => (
                  <Row key={i} label={`Up-line ${i + 1}${["st", "nd", "rd", "th", "th", "th"][i]}`} value={fmt(amt)} accent="text-cyan-300" />
                ))}
                <div className="pt-2 border-t border-white/10">
                  <Row label="ID holder" value={fmt(b.holder)} accent="text-emerald-300" bold />
                </div>
                <div className="pt-2 border-t border-white/10">
                  <Row label="Total payout" value={fmt(b.total)} accent="text-white" bold />
                </div>
              </div>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 mb-6 overflow-x-auto" data-testid="sc-matrix-tree">
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-1">
              <Network className="h-5 w-5 text-cyan-400" /> Matrix Level
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Global matrix (3-wide · {d.matrix.level_slots.join(" / ")}) filled by Silver Card grants in grant-time order. Level 3 completion pays the Silver Card.
            </p>
            {(!d.matrix.total_drops) ? (
              <p className="text-sm text-slate-500 py-12 text-center">No matrix boxes filled yet for this member.</p>
            ) : (
              <MatrixTree root={d.matrix.root} width={d.matrix.width} />
            )}
          </div>

          <div className="glass rounded-2xl p-6" data-testid="sc-cards-list">
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Gem className="h-5 w-5 text-cyan-400" /> Silver Cards
            </h3>
            {d.cards.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">This member has no Silver Cards.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                    <th className="py-2 pr-4">Granted</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Payout</th><th className="py-2 text-right">Completed</th>
                  </tr></thead>
                  <tbody>
                    {d.cards.map((c) => (
                      <tr key={c.id} className="border-b border-white/5 last:border-0">
                        <td className="py-3 pr-4 font-num text-slate-400">{new Date(c.granted_at).toLocaleDateString()}</td>
                        <td className="py-3 pr-4"><StatusPill status={c.status === "completed" ? "completed" : "pending"} /></td>
                        <td className="py-3 pr-4 font-num text-emerald-300">{c.payout ? fmt(c.payout) : "—"}</td>
                        <td className="py-3 text-right font-num text-slate-400">{c.completed_at ? new Date(c.completed_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-6">
            <SettlementHistory cards={d.cards} total={b?.total || 27} testid="sc-settlement" />
          </div>
        </>
      )}
    </div>
  );
}
